import { useEffect, useMemo, useRef, useState, type DragEvent, type JSX, type PointerEvent } from 'react';
import type { Id, Vec2 } from '../../core/model';
import type { ViewTarget } from '../../render/api';
import { Icon } from '../components/Icon';
import { ViewCanvas } from '../components/ViewCanvas';
import { useActions, useProject, useUiContext } from '../context';
import { isTypingTarget } from '../hotkeys/chords';
import { addSourceMenu, itemContextMenu } from '../panels/sources/itemMenu';
import { safely } from '../safe';
import { addDroppedFiles } from './dropFiles';
import type { Handle } from './dragMath';
import { createEditorController, type HandleRef, type PointerInfo } from './editorController';
import { Overlay } from './Overlay';
import { centeredViewport, fitViewport, screenToCanvas, zoomAt, type Viewport } from './viewport';

/** 'actual' shows one canvas pixel per CSS pixel, centred. */
export type ZoomMode = { type: 'fit' } | { type: 'actual' } | { type: 'free'; viewport: Viewport };

export interface EditorProps {
  sceneId: Id;
  target: ViewTarget;
  zoom: ZoomMode;
  onZoomChange(zoom: ZoomMode): void;
  /** Reports the effective scale so the toolbar can show a percentage. */
  onScale?(scale: number): void;
}

function parseHandle(target: EventTarget | null): HandleRef | null {
  const raw = target instanceof Element ? target.getAttribute('data-handle') : null;
  if (!raw) return null;
  if (raw === 'rotate') return { type: 'rotate' };
  const [x, y] = raw.split(',').map(Number);
  return { type: 'resize', handle: { x, y } as Handle };
}

/** The interactive scene editor: compositor view underneath, selection overlay on top. */
export function Editor({ sceneId, target, zoom, onZoomChange, onScale }: EditorProps): JSX.Element {
  const { studio, ui } = useUiContext();
  const actions = useActions();
  const root = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<Vec2>({ x: 0, y: 0 });
  const [dropping, setDropping] = useState(false);
  const [panning, setPanning] = useState(false);
  const video = useProject((p) => p.video);
  const empty = useProject((p) => (p.scenes[sceneId]?.items.length ?? 0) === 0);

  const canvas = useMemo(() => ({ x: video.width, y: video.height }), [video.width, video.height]);
  const viewport = useMemo(
    () =>
      zoom.type === 'fit'
        ? fitViewport(container, canvas)
        : zoom.type === 'actual'
          ? centeredViewport(container, canvas, 1)
          : zoom.viewport,
    [zoom, container, canvas],
  );
  const live = useRef({ viewport, sceneId, onZoomChange });
  live.current = { viewport, sceneId, onZoomChange };

  useEffect(() => onScale?.(viewport.scale), [onScale, viewport.scale]);

  const controller = useMemo(
    () =>
      createEditorController({
        studio,
        ui,
        actions,
        getSceneId: () => live.current.sceneId,
        getViewport: () => live.current.viewport,
        panTo: (next) => live.current.onZoomChange({ type: 'free', viewport: next }),
      }),
    [studio, ui, actions],
  );

  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const measure = (): void => {
      const rect = node.getBoundingClientRect();
      setContainer((prev) => (prev.x === rect.width && prev.y === rect.height ? prev : { x: rect.width, y: rect.height }));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);

    // React registers wheel listeners as passive; zooming must prevent page scroll.
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0015));
      live.current.onZoomChange({ type: 'free', viewport: zoomAt(live.current.viewport, factor, anchor) });
    };
    node.addEventListener('wheel', onWheel, { passive: false });

    let hovered = false;
    const onEnter = (): void => void (hovered = true);
    const onLeave = (): void => void (hovered = false);
    const onKey = (event: KeyboardEvent): void => {
      if (event.type === 'keydown' && event.key === 'Escape' && controller.cancel()) {
        event.preventDefault();
        return;
      }
      if (event.code !== 'Space' || isTypingTarget(event.target)) return;
      const held = event.type === 'keydown' && hovered;
      if (held) event.preventDefault();
      if (held !== controller.isSpaceHeld()) {
        controller.setSpaceHeld(held);
        setPanning(held);
      }
    };
    // The key-up never arrives if the window loses focus while Space is held.
    const onBlur = (): void => {
      if (!controller.isSpaceHeld()) return;
      controller.setSpaceHeld(false);
      setPanning(false);
    };
    window.addEventListener('blur', onBlur);
    node.addEventListener('pointerenter', onEnter);
    node.addEventListener('pointerleave', onLeave);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      observer?.disconnect();
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('pointerenter', onEnter);
      node.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, [controller]);

  const localPoint = (event: { clientX: number; clientY: number }): Vec2 => {
    const rect = root.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  const info = (event: { clientX: number; clientY: number; button: number; altKey: boolean; shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }): PointerInfo => {
    return {
      point: localPoint(event),
      button: event.button,
      alt: event.altKey,
      shift: event.shiftKey,
      ctrl: event.ctrlKey || event.metaKey,
    };
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.target instanceof Element && event.target.closest('button')) return;
    if (event.button !== 0 && event.button !== 1) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.currentTarget.focus({ preventScroll: true });
    if (event.button === 1) event.preventDefault();
    controller.pointerDown(info(event), parseHandle(event.target));
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDropping(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) return;
    const point = screenToCanvas(live.current.viewport, localPoint(event));
    void safely(studio, async () => {
      const created = await addDroppedFiles(studio, sceneId, files, point);
      if (created.length > 0) ui.getState().select(created);
    });
  };

  const openAddMenu = (anchor: HTMLElement): void => {
    const rect = anchor.getBoundingClientRect();
    ui.getState().openMenu({ x: rect.left, y: rect.bottom + 2, items: addSourceMenu(studio, ui, actions), returnFocus: anchor });
  };

  const sized = container.x > 0 && container.y > 0;
  return (
    <div
      ref={root}
      className={['editor', dropping ? 'dropping' : '', panning ? 'panning' : ''].join(' ')}
      tabIndex={0}
      role="application"
      aria-label="Scene editor. Arrow keys nudge the selected items."
      onPointerDown={onPointerDown}
      onPointerMove={(e) => controller.pointerMove(info(e))}
      onPointerUp={() => controller.pointerUp()}
      onPointerCancel={() => void controller.cancel()}
      onDoubleClick={(e) => controller.doubleClick(info(e))}
      onContextMenu={(e) => {
        e.preventDefault();
        const itemId = controller.contextTarget(info(e));
        ui.getState().openMenu({
          x: e.clientX,
          y: e.clientY,
          items: itemContextMenu(studio, ui, actions, itemId),
          returnFocus: root.current,
        });
      }}
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer.types).includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={onDrop}
    >
      {sized ? (
        <ViewCanvas
          target={target}
          label={target.type === 'preview' ? 'Preview picture' : 'Program picture'}
          className="editor-canvas"
          style={{
            left: viewport.offsetX,
            top: viewport.offsetY,
            width: canvas.x * viewport.scale,
            height: canvas.y * viewport.scale,
          }}
        />
      ) : null}
      {sized ? <Overlay sceneId={sceneId} viewport={viewport} controller={controller} /> : null}
      {empty ? (
        <div className="editor-empty">
          <div className="editor-empty-card">
            <span>No sources — add one with +</span>
            <button type="button" className="ui-btn primary" onClick={(e) => openAddMenu(e.currentTarget)}>
              <Icon name="plus" /> Add source
            </button>
            <span className="ui-dim">or drop image, video and audio files here</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
